import { apiRequest } from '@/domains/api';
import type { IPage } from './oversight-contracts';
import type {
  IMailLogQuery,
  IMailLogRow,
  IMailReply,
  IMailReplyQuery,
  IMailSuppression,
  IParticipantMailStatus,
  ISuppressionQuery
} from './mail-log-contracts';

/**
 * survey-api's mail log, suppressions, a participant's mail status and the replies (SR-523
 * stage 3). A suppression's add answers 409 when one is active already, its clear 409 when it was
 * cleared meanwhile.
 */

const ADMIN = '/api/v1/admin';

function withQuery(path: string, query: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) for (const each of value) params.append(key, String(each));
    else if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `${path}?${text}` : path;
}

export const mailLogApi = {
  async log(query: IMailLogQuery, signal?: AbortSignal): Promise<IPage<IMailLogRow>> {
    const answer = await apiRequest<IPage<IMailLogRow>>(withQuery(`${ADMIN}/mail-log`, query), { signal });
    return { ...answer, items: (answer.items ?? []).map((row) => ({ ...row, cc: row.cc ?? [], replies: row.replies ?? 0, tags: row.tags ?? [] })) };
  },

  async suppressions(query: ISuppressionQuery, signal?: AbortSignal): Promise<IPage<IMailSuppression>> {
    const answer = await apiRequest<IPage<IMailSuppression>>(withQuery(`${ADMIN}/mail-suppressions`, query), {
      signal
    });
    return { ...answer, items: (answer.items ?? []).map((row) => ({ ...row, participants: row.participants ?? [] })) };
  },

  /** A `staff` suppression: bulk mail skips the address from now on. */
  suppress(address: string, note: string): Promise<IMailSuppression> {
    return apiRequest<IMailSuppression>(`${ADMIN}/mail-suppressions`, { method: 'POST', body: { address, note } });
  },

  /** Bulk mail reaches the address again; the note says why (consent). */
  clear(id: string, note: string): Promise<unknown> {
    return apiRequest<unknown>(`${ADMIN}/mail-suppressions/${encodeURIComponent(id)}/clear`, {
      method: 'POST',
      body: { note }
    });
  },

  async mailStatus(participantId: string, signal?: AbortSignal): Promise<IParticipantMailStatus> {
    const answer = await apiRequest<IParticipantMailStatus>(
      `${ADMIN}/participants/${encodeURIComponent(participantId)}/mail-status`,
      { signal }
    );
    return { addresses: answer.addresses ?? [] };
  },

  async reply(id: string, signal?: AbortSignal): Promise<IMailReply> {
    const answer = await apiRequest<IMailReply>(`${ADMIN}/mail-replies/${encodeURIComponent(id)}`, { signal });
    return { ...answer, attachments: answer.attachments ?? [] };
  },

  async replies(query: IMailReplyQuery, signal?: AbortSignal): Promise<IPage<IMailReply>> {
    const answer = await apiRequest<IPage<IMailReply>>(withQuery(`${ADMIN}/mail-replies`, query), { signal });
    return { ...answer, items: (answer.items ?? []).map((reply) => ({ ...reply, attachments: reply.attachments ?? [] })) };
  },

  /** An unmatched reply dealt with: out of the open queue, the note kept. Nothing else is done. */
  review(id: string, note: string): Promise<unknown> {
    return apiRequest<unknown>(`${ADMIN}/mail-replies/${encodeURIComponent(id)}/review`, {
      method: 'POST',
      body: { note: note || null }
    });
  }
};
